/**
 * The report wait — PRD §3.
 *
 * "The user sees which opportunity is being worked on, a candid wait message
 * (duration to be measured), a route back to the complete list, and an
 * optional email field to recover a result if they leave."
 *
 * And the rule that shapes the layout: "If the report becomes ready while a
 * preview is open, close the preview and replace that panel with a large
 * 'Your research is ready' message and 'Open research' action. The ready state
 * takes priority; no preview can hide it."
 */

import React, { useState } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CapabilityPreview from './CapabilityPreview';
import { CAPABILITY_PANELS, copyFor, SHARED } from './audienceCopy';

export default function ReportWaitStep({
  audience, storyTitle, status, onOpenReport, onBackToList, onRetry, onRequestDemo,
}) {
  const copy = copyFor(audience);
  const [openPanel, setOpenPanel] = useState(null);
  const ready = status === 'ready';
  const failed = status === 'failed';

  // §3 — the ready state takes priority, so an open preview is simply not
  // rendered once the report lands. Closing it in an effect would be the same
  // outcome one frame later, with a chance of the preview winning the race.
  const previewKey = ready || failed ? null : openPanel;

  return (
    <>
      <div className="cmp-actions" style={{ marginTop: 0, marginBottom: 24 }}>
        <button type="button" className="cmp-btn cmp-btn--secondary" onClick={onBackToList}>
          ← {SHARED.backToOpportunities}
        </button>
      </div>

      {/* The eyebrow is part of the truthful state too — it said "preparing
          research" over a heading announcing the research was ready. */}
      <p className="cmp-eyebrow">{ready ? 'Research' : failed ? 'Research' : 'Preparing research'}</p>
      <h1 className="cmp-title">
        {ready ? copy.reportReadyTitle : failed ? 'We could not prepare this research.' : copy.reportWait}
      </h1>
      {/* Which opportunity is being worked on (§3). */}
      {storyTitle && <p className="cmp-lede">{storyTitle}</p>}

      <div className="cmp-split">
        <div>
          {failed ? (
            <>
              <div className="cmp-notice">{SHARED.reportFailed}</div>
              <div className="cmp-actions">
                <button type="button" className="cmp-btn cmp-btn--primary" onClick={onRetry}>
                  {SHARED.retry}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="cmp-eyebrow" style={{ marginTop: 8 }}>The wider platform</p>
              <h2 className="cmp-section-title">Go further when you are ready</h2>
              {/* Optional, and the visitor controls the exploration (§3). */}
              <div className="cmp-panel-list">
                {CAPABILITY_PANELS.map((key) => {
                  const panel = copy.capabilities[key];
                  const isOpen = previewKey === key;
                  return (
                    <div className={`cmp-capability ${isOpen ? 'cmp-capability--open' : ''}`} key={key}>
                      <h3 className="cmp-capability-title">{panel.title}</h3>
                      <p className="cmp-capability-body">{panel.body}</p>
                      <button
                        type="button"
                        className="cmp-link-btn"
                        onClick={() => setOpenPanel(isOpen ? null : key)}
                      >
                        {isOpen ? 'Hide preview' : `${SHARED.seePreview} →`}
                      </button>
                      <CapabilityPreview panelKey={key} title={panel.title} skin="cmp" revealed={isOpen} />
                    </div>
                  );
                })}
              </div>

              {/* §3 — the guided-demo request stays visually secondary. Absent
                  entirely until phase 3 builds the form behind it: a button
                  that goes nowhere is the failure a silent handler produces. */}
              {onRequestDemo && (
                <div className="cmp-actions">
                  <button type="button" className="cmp-btn cmp-btn--secondary" onClick={onRequestDemo}>
                    {SHARED.requestAGuidedDemo}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className={`cmp-panel${!ready && !failed ? ' cmp-panel--busy' : ''}`}>
          <div className="cmp-panel-content">
            {ready ? (
              <>
                {/* A mark, not a second heading. The page title above already
                    says the research is ready; repeating the sentence put it
                    on screen twice. The tick carries the same meaning without
                    saying it again, and gives the panel something to be. */}
                <div className="cmp-panel-mark" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="20" height="20">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <p className="cmp-panel-sub">Open it whenever you are ready.</p>
                <button type="button" className="cmp-btn cmp-btn--primary" onClick={onOpenReport}>
                  {SHARED.openResearch}
                  <ArrowForwardIcon style={{ fontSize: 16 }} />
                </button>
              </>
            ) : failed ? (
              <h2 className="cmp-panel-title">Stopped before finishing</h2>
            ) : (
              <>
                {/* Activity indicator — present only while there is activity.
                    Gone the moment the job settles, ready or failed. */}
                <div className="cmp-rings" aria-hidden="true">
                  <span className="cmp-ring cmp-ring--1" />
                  <span className="cmp-ring cmp-ring--2" />
                  <span className="cmp-ring cmp-ring--3" />
                  <span className="cmp-ring cmp-ring--core" />
                  <span className="cmp-ring cmp-ring--arc" />
                </div>
                {/* §3 — "a candid wait message (duration to be measured)". No
                    number until there is a measured one. */}
                <h2 className="cmp-panel-title">Reading the sources</h2>
                <p className="cmp-panel-sub">
                  This takes a few minutes. You can keep exploring — it carries on without you.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
